import React, { useState } from 'react';
import { Wand2, Upload, LayoutGrid, FileText, Check, Sparkles } from 'lucide-react';
import { ColorSwatch, CustomProject } from '../../types/productBuilder';
import { useAuth } from '../../lib/AuthContext';

interface LaunchpadProps {
  onStartProject: (project: CustomProject) => void;
  presetSwatches: ColorSwatch[];
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const Launchpad: React.FC<LaunchpadProps> = ({ onStartProject, presetSwatches, onAddLog }) => {
  const { user } = useAuth();
  const [activeSubView, setActiveSubView] = useState<'selection' | 'guided' | 'upload'>('selection');

  // Guided Form states
  const [wizardAnswers, setWizardAnswers] = useState({
    name: '',
    industry: 'Technology & AI',
    projectType: 'Responsive SaaS Console',
    swatch: presetSwatches[0],
    typography: 'Space Grotesk',
    pages: ['Main Dashboard Overview', 'Operations Sync Portal', 'Service Security Ticker']
  });

  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);

  const handleGuidedSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const projectName = wizardAnswers.name.trim() || 'NexoraX Managed Platform';
    const initPages = wizardAnswers.pages.map((t, idx) => ({
      id: `pw_page_${Date.now()}_${idx}`,
      title: t,
      url: '/' + t.toLowerCase().replace(/[^a-z0-9]/g, '-'),
      layoutSections: idx === 0 
        ? ['Atmospheric Header', 'Sub-Penny Billing Activity', 'Custom System SLA Lock', 'Direct Dispatch Intake Form']
        : ['Sub-Page Workspace Header', 'Telemetry Logs Sync List', 'Client Resource Footer']
    }));

    const project: CustomProject = {
      id: 'proj_' + Date.now(),
      name: projectName,
      industry: wizardAnswers.industry,
      projectType: wizardAnswers.projectType,
      status: 'Draft',
      pages: initPages,
      colors: wizardAnswers.swatch,
      typography: wizardAnswers.typography,
      createdAt: new Date().toISOString()
    };

    onAddLog('Product Builder', `Synthesized custom layout config for "${projectName}" via Launchpad Wizard`, 'SUCCESS', 'Custom pages layout instantiated.');
    onStartProject(project);
  };

  const handleImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!user) {
      alert("UNAUTHORIZED: Sign in or authenticate status is required to upload wireframe assets and create cloud-saved custom layouts.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert("Validation failed: Maximum file size allowed is 2MB.");
      return;
    }

    setScanning(true);
    setScanProgress(15);

    try {
      setScanProgress(35);
      const idToken = await user.getIdToken();

      const project: CustomProject = {
        id: 'proj_' + Date.now(),
        name: 'Inspired Layout (Scanned Wireframe)',
        industry: 'AI & Data Transformation',
        projectType: 'Architectonic Control Deck',
        status: 'Draft',
        pages: [
          { id: 'img_p1', title: 'Interactive Console Main', url: '/', layoutSections: ['Atmospheric Header', 'Live SQL Telemetry Monitoring', 'Dynamic Inflow Charts Grid', 'Security Cryptography Lock'] },
          { id: 'img_p2', title: 'Compliance Audits Logs', url: '/compliance', layoutSections: ['Default Layout Header', 'Audit Logs Table View', 'Risk Indicator Details'] }
        ],
        colors: presetSwatches[1],
        typography: 'JetBrains Mono',
        createdAt: new Date().toISOString()
      };

      const createResponse = await fetch('/api/product-builder/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(project)
      });
      const createdData = await createResponse.json().catch(() => null);
      if (!createResponse.ok || !createdData?.project?.id) {
        throw new Error(createdData?.error || `Project creation failed (${createResponse.status}).`);
      }

      const projectId = createdData.project.id;
      const formData = new FormData();
      formData.append('inspirationImage', file);
      const res = await fetch(`/api/product-builder/upload-inspiration?projectId=${encodeURIComponent(projectId)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        body: formData
      });
      setScanProgress(75);
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && data.fileUrl) {
        setUploadedImage(data.fileUrl);
        setScanProgress(100);
        setScanning(false);

        const securedProject = { ...project, id: projectId, uploadedInspirationImage: data.fileUrl };

        onAddLog('Product Builder', 'Processed scanned visual draft in Galaxy sandbox', 'SUCCESS', 'Spatial grids mapped successfully and image safely uploaded to private server storage.');
        onStartProject(securedProject);

      } else {
        alert(`Validation/Scan failed: ${data?.error || `Upload rejected (${res.status}).`}`);
        setScanning(false);
      }
    } catch (err: any) {
      console.error(err);
      alert(`Upload error: ${err.message}`);
      setScanning(false);
    }
  };

  const startWithTemplate = (id: string, name: string, ind: string, type: string, swatchIdx: number, font: string) => {
    const project: CustomProject = {
      id: 'proj_tpl_' + Date.now(),
      name: `Custom ${name}`,
      templateId: id,
      industry: ind,
      projectType: type,
      status: 'Draft',
      pages: [
        { id: 'tpl_p1', title: 'Showcase Hub', url: '/', layoutSections: ['Atmospheric Header', 'Responsive Cards Feature Matrix', 'Zero-Fund Billing Ledger', 'System Sign-Up Box'] },
        { id: 'tpl_p2', title: 'Telemetry Center', url: '/system', layoutSections: ['Sub-Page Header Navigation', 'Live Core Nodes Stat Line', 'System Audit Logger Grid'] }
      ],
      colors: presetSwatches[swatchIdx],
      typography: font,
      createdAt: new Date().toISOString()
    };

    onAddLog('Product Builder', `Loaded template "${name}" from catalog`, 'SUCCESS', 'Mounted layout blueprint.');
    onStartProject(project);
  };

  const startBlank = () => {
    const project: CustomProject = {
      id: 'proj_blank_' + Date.now(),
      name: 'Blank Custom Layout Sandbox',
      industry: 'Custom Enterprise Services',
      projectType: 'High-Density Control Layout',
      status: 'Draft',
      pages: [
        { id: 'blank_p1', title: 'Main Panel', url: '/', layoutSections: ['Atmospheric Header', 'Modular Section Container', 'Client Resource Footer'] }
      ],
      colors: presetSwatches[0],
      typography: 'Space Grotesk',
      createdAt: new Date().toISOString()
    };

    onAddLog('Product Builder', 'Initialized empty custom layout sandbox', 'SUCCESS', 'Seeded layout matrix.');
    onStartProject(project);
  };

  return (
    <div className="npx-builder-launchpad-container space-y-6">
      {activeSubView === 'selection' && (
        <div className="space-y-8 animate-fade-in text-left">
          <div className="border-b border-white/5 pb-4">
            <h2 className="text-sm font-mono tracking-widest text-[#00ffff] uppercase font-black">1. Launchpad Entrance</h2>
            <p className="text-xs text-zinc-400 mt-1">Deploy a new layout blueprint in our isolated preview sandbox using templates, step assistance, or layout scans.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setActiveSubView('guided')}
              className="p-6 rounded-2xl bg-black/40 hover:bg-zinc-950 border border-white/5 hover:border-purple-500/30 transition-all cursor-pointer flex flex-col justify-between h-[180px] group text-left"
            >
              <div>
                <Wand2 className="w-7 h-7 text-purple-400 group-hover:text-white transition-colors" />
                <h3 className="text-xs font-bold text-white uppercase font-mono tracking-wider mt-3">Guided Assistant</h3>
                <p className="text-[10px] text-zinc-500 mt-1 lines-clamp-2 leading-relaxed">Follow a prompt-free guided sequence to choose features and set pages instantly.</p>
              </div>
              <span className="text-[9px] text-purple-400 font-mono tracking-wider font-bold">Launch Assistant →</span>
            </button>

            <button
              onClick={() => setActiveSubView('upload')}
              className="p-6 rounded-2xl bg-black/40 hover:bg-zinc-950 border border-white/5 hover:border-cyan-500/30 transition-all cursor-pointer flex flex-col justify-between h-[180px] group text-left"
            >
              <div>
                <Upload className="w-7 h-7 text-cyan-400 group-hover:text-white transition-colors" />
                <h3 className="text-xs font-bold text-white uppercase font-mono tracking-wider mt-3">Inspiration scan</h3>
                <p className="text-[10px] text-zinc-500 mt-1 lines-clamp-2 leading-relaxed">Upload any geometric layout sketch or diagram. Mapped contour layouts will generate instantly.</p>
              </div>
              <span className="text-[9px] text-[#00ffff] font-mono tracking-wider font-bold">Upload Sketch →</span>
            </button>

            <button
              onClick={startBlank}
              className="p-6 rounded-2xl bg-black/40 hover:bg-zinc-950 border border-white/5 hover:border-yellow-500/30 transition-all cursor-pointer flex flex-col justify-between h-[180px] group text-left"
            >
              <div>
                <FileText className="w-7 h-7 text-yellow-400 group-hover:text-white transition-colors" />
                <h3 className="text-xs font-bold text-white uppercase font-mono tracking-wider mt-3">Start From Scratch</h3>
                <p className="text-[10px] text-zinc-500 mt-1 lines-clamp-2 leading-relaxed">Initialize a pristine blank layout container with basic standard sections to construct from ground up.</p>
              </div>
              <span className="text-[9px] text-yellow-500 font-mono tracking-wider font-bold">Bootstrap Blank →</span>
            </button>
          </div>

          <div className="space-y-4 pt-4 border-t border-white/5 text-left">
            <h3 className="text-xs font-bold font-mono uppercase tracking-wider text-zinc-400 flex items-center gap-1.5">
              <LayoutGrid size={13} className="text-[#00ffff]" />
              <span>Aesthetic Template Starters</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4.5">
              {[
                { id: 'ndp-1', name: 'AI Business Starter', ind: 'Enterprise & AI Solutions', type: 'Responsive Launch Suite', sw: 0, fn: 'Space Grotesk' },
                { id: 'ndp-2', name: 'E-commerce Master', ind: 'B2C Retail Systems', type: 'Optimized E-Shop Grid', sw: 3, fn: 'Outfit' },
                { id: 'ndp-3', name: 'SaaS Dashboard Console', ind: 'Technical Saas Infra', type: 'High-Density Control Deck', sw: 1, fn: 'JetBrains Mono' },
                { id: 'ndp-4', name: 'Premium Agency Deck', ind: 'Creative Consultancy', type: 'Aesthetic Editorial Space', sw: 2, fn: 'Playfair Display' }
              ].map((tpl) => (
                <div
                  key={tpl.id}
                  onClick={() => startWithTemplate(tpl.id, tpl.name, tpl.ind, tpl.type, tpl.sw, tpl.fn)}
                  className="p-4 bg-zinc-950/60 rounded-xl border border-white/5 hover:border-zinc-800 transition-all cursor-pointer space-y-2.5 text-left flex flex-col justify-between group"
                >
                  <div className="space-y-1">
                    <span className="text-[7.5px] font-mono tracking-widest text-[#00ffff] font-bold block uppercase">{tpl.ind}</span>
                    <h4 className="text-xs font-bold text-white uppercase font-sans group-hover:text-[#00ffff] transition-colors">{tpl.name}</h4>
                    <p className="text-[9.5px] text-zinc-500 leading-normal">{tpl.type}</p>
                  </div>
                  <div className="text-[8.5px] text-zinc-400 font-mono flex items-center justify-between border-t border-white/5 pt-1.5">
                    <span>Font: {tpl.fn}</span>
                    <span className="text-[8px] bg-zinc-900 border border-white/5 px-1.5 py-0.5 rounded uppercase font-black text-cyan-400">Load</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeSubView === 'guided' && (
        <form onSubmit={handleGuidedSubmit} className="space-y-6 animate-fade-in text-left bg-black/20 border border-white/5 rounded-2xl p-6">
          <div className="flex justify-between items-center border-b border-white/5 pb-3">
            <h3 className="text-xs font-bold text-[#00ffff] uppercase font-mono tracking-wider flex items-center gap-1.5">
              <Wand2 size={14} className="animate-pulse" />
              <span>Prompt-free Guided Constructor</span>
            </h3>
            <button
              type="button"
              onClick={() => setActiveSubView('selection')}
              className="text-[10px] font-mono text-zinc-400 hover:text-white"
            >
              ✕ Back
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 text-left">
              <label className="text-[10px] font-mono text-zinc-400 uppercase font-bold">Project Name / Identity</label>
              <input
                type="text"
                required
                value={wizardAnswers.name}
                onChange={(e) => setWizardAnswers({ ...wizardAnswers, name: e.target.value })}
                placeholder="e.g. NexoraX Healthcare Hub"
                className="w-full bg-black/40 border border-zinc-900 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-purple-500"
              />
            </div>

            <div className="space-y-1.5 text-left">
              <label className="text-[10px] font-mono text-zinc-400 uppercase font-bold">Candidate Industry Vertical</label>
              <select
                value={wizardAnswers.industry}
                onChange={(e) => setWizardAnswers({ ...wizardAnswers, industry: e.target.value })}
                className="w-full bg-black/40 border border-zinc-900 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-purple-500"
              >
                <option>Technology & AI</option>
                <option>B2B Wholesale Trade</option>
                <option>Healthcare & Biotech</option>
                <option>Aesthetic Design Studio</option>
                <option>Real Estate & Space</option>
              </select>
            </div>

            <div className="space-y-1.5 text-left">
              <label className="text-[10px] font-mono text-zinc-400 uppercase font-bold">System Archetype</label>
              <input
                type="text"
                required
                value={wizardAnswers.projectType}
                onChange={(e) => setWizardAnswers({ ...wizardAnswers, projectType: e.target.value })}
                placeholder="e.g. High-Density Enterprise Operations Panel"
                className="w-full bg-black/40 border border-zinc-900 rounded-lg p-2 text-xs text-white focus:outline-none"
              />
            </div>

            <div className="space-y-1.5 text-left">
              <label className="text-[10px] font-mono text-zinc-400 uppercase font-bold">Primary Font Selection</label>
              <select
                value={wizardAnswers.typography}
                onChange={(e) => setWizardAnswers({ ...wizardAnswers, typography: e.target.value })}
                className="w-full bg-black/40 border border-zinc-900 rounded-lg p-2 text-xs text-white focus:outline-none"
              >
                <option value="Space Grotesk">Space Grotesk (Tech Futuristic)</option>
                <option value="Inter">Inter (Swiss Modernist)</option>
                <option value="JetBrains Mono">JetBrains Mono (High Density Data)</option>
                <option value="Outfit">Outfit (Clean High-Contrast)</option>
                <option value="Playfair Display">Playfair Display (Serif Elegance)</option>
              </select>
            </div>
          </div>

          <div className="space-y-3 pt-3 border-t border-white/5">
            <h4 className="text-[10px] font-mono text-zinc-400 uppercase font-bold">Construct Home Components Skeleton</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                'Main Dashboard Overview',
                'Operations Sync Portal',
                'Service Security Ticker',
                'Physical Database Ledger',
                'Client Support Dispatcher'
              ].map((tag) => {
                const active = wizardAnswers.pages.includes(tag);
                return (
                  <button
                    type="button"
                    key={tag}
                    onClick={() => {
                      if (active) {
                        setWizardAnswers({ ...wizardAnswers, pages: wizardAnswers.pages.filter(p => p !== tag) });
                      } else {
                        setWizardAnswers({ ...wizardAnswers, pages: [...wizardAnswers.pages, tag] });
                      }
                    }}
                    className={`p-2.5 rounded-lg border text-left flex items-center justify-between text-[9px] transition-all cursor-pointer ${active ? 'border-purple-500 bg-purple-500/10 text-white' : 'border-zinc-900 bg-black/20 hover:border-zinc-800 text-zinc-400'}`}
                  >
                    <span>{tag}</span>
                    {active && <Check size={10} className="text-purple-400" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-white/5">
            <button
              type="submit"
              className="px-5 py-2.5 bg-gradient-to-r from-purple-900 to-indigo-950 hover:bg-gradient-to-r hover:from-purple-800 hover:to-indigo-900 border border-purple-500/30 text-white rounded-xl text-[10.5px] uppercase font-mono tracking-wider font-bold transition-all cursor-pointer flex items-center gap-1.5"
            >
              <Sparkles size={13} className="text-purple-400" />
              <span>Instantiate Layout Structure</span>
            </button>
          </div>
        </form>
      )}

      {activeSubView === 'upload' && (
        <div className="space-y-6 animate-fade-in text-left bg-black/20 border border-white/5 rounded-2xl p-6">
          <div className="flex justify-between items-center border-b border-white/5 pb-3">
            <h3 className="text-xs font-bold text-[#00ffff] uppercase font-mono tracking-wider flex items-center gap-1.5">
              <Upload size={14} className="text-cyan-400" />
              <span>Spatial Contour Wireframe Analyzer</span>
            </h3>
            <button
              type="button"
              onClick={() => setActiveSubView('selection')}
              className="text-[10px] font-mono text-zinc-400 hover:text-white"
            >
              ✕ Back
            </button>
          </div>

          {!uploadedImage ? (
            <div className="border border-dashed border-zinc-800 hover:border-[#00ffff]/30 bg-black/40 p-8 rounded-2xl text-center space-y-3 transition-all cursor-pointer relative">
              <input
                type="file"
                id="contour-img-uploader"
                accept="image/*"
                onChange={handleImageFile}
                className="hidden"
              />
              <label htmlFor="contour-img-uploader" className="cursor-pointer block space-y-2">
                <Upload size={24} className="mx-auto text-cyan-400" />
                <p className="text-xs font-mono font-bold text-white uppercase">Upload Visual Layout Sketch</p>
                <p className="text-[10px] text-zinc-500 max-w-xs mx-auto leading-normal font-sans">
                  Provide any image of basic columns, boxes, layouts, or wireframes. This isolated scanner translates columns and fonts instantly.
                </p>
              </label>
            </div>
          ) : (
            <div className="space-y-4">
              {scanning ? (
                <div className="space-y-2 py-4">
                  <div className="flex justify-between items-center text-[10px] font-mono text-[#00ffff]">
                    <span>ANALYZING SPATIAL WIREFRAME MATRIX...</span>
                    <span>{scanProgress}%</span>
                  </div>
                  <div className="w-full bg-zinc-950 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-[#00ffff] h-full transition-all duration-300" style={{ width: `${scanProgress}%` }} />
                  </div>
                  <p className="text-[8px] text-zinc-500 font-mono leading-none">Scoping isolated boundaries & layout column density anchors...</p>
                </div>
              ) : (
                <div className="text-center font-mono py-1">
                  <span className="text-[10px] text-[#00ff00] uppercase font-black">✓ Spatial analysis complete. Layout Loaded!</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
